import { InfoBox, InfoBoxProps } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  GetTraceabilityCampaignStatsResponseDto,
  VerifyRawOperationsFileResponseDto,
} from "~/gen/api"
import {
  EligibleProductsInfoBoxContent,
  EligibleProductsInfoBoxContentProps,
} from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal/EligibleProductsInfoBoxContent"
import { RawOperationsFileError } from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal/utils"

export type TraceabilityStatsInfoBoxProps = {
  stats: GetTraceabilityCampaignStatsResponseDto
  onFileChange: (file: File) => void
  rawOperationsFileErrors: RawOperationsFileError[]
  setRawOperationsFileErrors: React.Dispatch<
    React.SetStateAction<RawOperationsFileError[]>
  >
  isRawOperationsFileLoading: boolean
  setRawOperationsChecked: React.Dispatch<React.SetStateAction<boolean>>
  rawOperationsChecked: boolean
  rawOperationsUploadedFile: VerifyRawOperationsFileResponseDto | undefined
  setRawOperationsUploadedFile: React.Dispatch<
    React.SetStateAction<VerifyRawOperationsFileResponseDto | undefined>
  >
}

export const TraceabilityStatsInfoBoxes = ({
  stats,
  onFileChange,
  rawOperationsFileErrors,
  isRawOperationsFileLoading,
  setRawOperationsFileErrors,
  setRawOperationsChecked,
  rawOperationsChecked,
  rawOperationsUploadedFile,
  setRawOperationsUploadedFile,
}: TraceabilityStatsInfoBoxProps) => {
  const { t } = useTranslation("traceability")

  const getMessageContentProps = useCallback<
    (message: string) => EligibleProductsInfoBoxContentProps
  >(
    (message) => ({
      message,
      onFileChange,
      rawOperationsFileErrors,
      loading: isRawOperationsFileLoading,
      setRawOperationsFileErrors,
      setRawOperationsChecked,
      rawOperationsChecked,
      rawOperationsUploadedFile,
      setRawOperationsUploadedFile,
    }),
    [
      isRawOperationsFileLoading,
      onFileChange,
      rawOperationsFileErrors,
      setRawOperationsFileErrors,
      setRawOperationsChecked,
      rawOperationsChecked,
      rawOperationsUploadedFile,
      setRawOperationsUploadedFile,
    ]
  )

  const eligibleProductsInfoBoxProps = useMemo(() => {
    if (stats.eligibleProductsCount === 1) {
      return {
        title: t("launch.modal.eligibleProducts.infoBox.title.single"),
        message: (
          <EligibleProductsInfoBoxContent
            {...getMessageContentProps(
              t("launch.modal.eligibleProducts.infoBox.description.single", {
                estimatedTraceabilityCoverage:
                  stats.estimatedTraceabilityCoverage.estimated,
              })
            )}
          />
        ),
        bg: "var(--tfg-bg-color-notice)",
      }
    }

    return {
      title: t("launch.modal.eligibleProducts.infoBox.title.multiple", {
        eligibleProductsCount: stats.eligibleProductsCount,
      }),
      message: (
        <EligibleProductsInfoBoxContent
          {...getMessageContentProps(
            t("launch.modal.eligibleProducts.infoBox.description.multiple", {
              eligibleProductsCount: stats.eligibleProductsCount,
              estimatedTraceabilityCoverage:
                stats.estimatedTraceabilityCoverage.estimated,
            })
          )}
        />
      ),
      bg: "var(--tfg-bg-color-notice)",
    }
  }, [stats, t, getMessageContentProps])

  const pendingProductsInfoBoxProps = useMemo<InfoBoxProps>(() => {
    if (stats.pendingProductsCount === 1) {
      return {
        title: t("launch.modal.pendingProducts.infoBox.title.single"),
        message: t("launch.modal.pendingProducts.infoBox.description.single"),
        bg: "var(--tfg-bg-color-warning)",
      }
    }

    return {
      title: t("launch.modal.pendingProducts.infoBox.title.multiple", {
        pendingProductsCount: stats.pendingProductsCount,
      }),
      message: t("launch.modal.pendingProducts.infoBox.description.multiple", {
        pendingProductsCount: stats.pendingProductsCount,
      }),
      bg: "var(--tfg-bg-color-warning)",
    }
  }, [stats, t])

  const fullTraceabilityInfoBoxProps = useMemo<InfoBoxProps>(() => {
    return {
      title: t("launch.modal.fullTraceability.infoBox.title"),
      message: t("launch.modal.fullTraceability.infoBox.description"),
      bg: "var(--tfg-bg-color-success)",
    }
  }, [t])

  const renderResolver = useMemo(
    () => [
      {
        props: eligibleProductsInfoBoxProps,
        condition: stats.eligibleProductsCount > 0,
        key: "eligibleProducts",
      },
      {
        props: pendingProductsInfoBoxProps,
        condition: stats.pendingProductsCount > 0,
        key: "pendingProducts",
      },
      {
        props: fullTraceabilityInfoBoxProps,
        condition: stats.tracedProductsCount === stats.totalProducts,
        key: "fullTraceability",
      },
    ],
    [
      eligibleProductsInfoBoxProps,
      fullTraceabilityInfoBoxProps,
      pendingProductsInfoBoxProps,
      stats,
    ]
  )

  return (
    <>
      {renderResolver
        .map(({ props, condition, key }) => {
          return condition ? (
            <InfoBox
              key={key}
              {...props}
              my="sm"
              messageStyles={{ marginLeft: "-1.5rem" }}
            />
          ) : null
        })
        .filter(Boolean)}
    </>
  )
}
